import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import {
    AppStartupService,
    BaseComponent,
    DepartureArrivalTime,
    DisruptionFilters,
    DisruptionMessage,
    DisruptionMessageResponse,
    DisruptionMessagesService,
    DisruptionMessagesSummary
} from '@ridango/core';
import Env from '@ridango/core/env';
import { ModalController, ToastController, ToggleCustomEvent } from '@ionic/angular';
import { map, startWith, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ApplicationFacade, CustomerFacade, NotificationFacade } from '@ridango/components';
import { TranslateService } from '@ngx-translate/core';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Temporal } from '@js-temporal/polyfill';
import { ActivatedRoute, Router } from '@angular/router';
import { DisruptionMessagesFilterModalComponent, DisruptionMessagesModalType } from '@ridango/widgets';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';
import { AuthService } from '@ridango/core/lib/services/auth.service';
import { Subject } from 'rxjs';
import { AddEmailModalComponent } from '@ridango/widgets';
import { EmailModalService } from '@ridango/widgets';

@Component({
    selector: 'gl-disruption-messages-page',
    templateUrl: './disruption-messages-page.component.html',
    styleUrls: ['./disruption-messages-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DisruptionMessagesPageComponent extends BaseComponent implements OnInit, OnDestroy {
    messagesSummary: DisruptionMessagesSummary[];
    messagesPackage: DisruptionMessageResponse;
    messages: DisruptionMessage[] = [];
    allMessages: DisruptionMessage[] = [];
    DisruptionFilters = DisruptionFilters;
    customFilters: DisruptionFilters[] = this.env.DISRUPTION_MESSAGES?.FILTERS;
    showAccordionSummary = false;
    hideBackButton = this.env.DISRUPTION_MESSAGES?.HIDE_BACK_BUTTON;
    showDatePicker = false;
    showAccordionFilters = true;
    isCollapsedSummary = 'summary';
    isCollapsedFilters = '';
    countActiveAlert: number;
    timezone: string;
    public maxDate: string;
    timeZone = this.appStartupService.getTimezone();
    resetFilter = this.env.DISRUPTION_MESSAGES?.SHOW_ALL_ALERTS_ON_OPEN;
    public dateValue: DepartureArrivalTime = this.resetFilter
        ? { datetime: null }
        : { datetime: Temporal.Now.plainDateISO(this.timeZone).toString() };
    public date = Temporal.Now.zonedDateTimeISO(this.timeZone).toPlainDate();
    categories: string[];
    chosenCategories: string[] = [];
    directions: string[] = [];
    chosenDirections: string[] = [];
    routes: { routeShortName: string; routeId: number | null }[];
    chosenRoutes: string[] = [];
    upcomingAlerts: boolean = this.customFilters?.includes(DisruptionFilters.UPCOMING) ?? true;
    assetsPath$ = this.applicationFacade.assetsPath$;
    disruptionMessagesModalType = DisruptionMessagesModalType;
    isDesktopView = false;
    private isModalOpen = false;
    private readonly FILTER_ALERTS = 'Filter alerts';
    private readonly TRAFFIC_INFO = 'Traffic info';
    private notificationClick$ = new Subject<Event>();
    isLoggedIn$ = this.authService.logoutBroadcast$.pipe(
        startWith(null),
        map(() => this.authService.getLoginStatus())
    );
    @ViewChild('emailModal') emailModal: AddEmailModalComponent;

    constructor(
        @Inject('ENV') public env: Env,
        private disruptionMessagesService: DisruptionMessagesService,
        private modalCtrl: ModalController,
        private applicationFacade: ApplicationFacade,
        private translate: TranslateService,
        private breakpointObserver: BreakpointObserver,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
        private appStartupService: AppStartupService,
        private analyticsService: AnalyticsService,
        private authService: AuthService,
        private toastController: ToastController,
        private customerFacade: CustomerFacade,
        private router: Router,
        private notification: NotificationFacade,
        private emailModalService: EmailModalService
    ) {
        super();
    }

    ngOnInit(): void {
        this.setupNotificationClickHandler();
        this.analyticsService.eventTrack(TrackedEventAction.OPENED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: this.TRAFFIC_INFO
        });
        const getAllOrTodayAlertsFilter = this.resetFilter
            ? {}
            : { dateTime: Temporal.Now.instant().toZonedDateTimeISO(this.timeZone).epochSeconds };

        this.timezone = this.appStartupService.getTimezone();
        this.maxDate = Temporal.Now.plainDateISO(this.timezone)
            .add({ years: 1 })
            .with({ month: 12, day: 31 })
            .toString();
        this.disruptionMessagesService
            .getMessages(getAllOrTodayAlertsFilter)
            .pipe(take(1))
            .subscribe((response) => {
                this.messagesPackage = response;
                this.messages = this.messagesPackage.messages;
                this.messagesSummary = this.disruptionMessagesService.getSummary(this.messagesPackage.messages);
                this.categories = this.disruptionMessagesService.getCategories(this.messagesPackage.messages);
                this.routes = this.disruptionMessagesService.getRoutes(this.messagesPackage.messages);
                this.countActiveAlert = this.messagesSummary.reduce((prev, curr) => prev + curr.amount, 0);
                this.handleCollapsedStatus();
                this.showDatePicker = true;
                this.cdr.detectChanges();
            });
        this.disruptionMessagesService
            .getMessages()
            .pipe(take(1))
            .subscribe((response) => {
                this.allMessages = response.messages;
                this.cdr.detectChanges();
            });
        this.breakpointObserver
            .observe(['(max-width: 768px)'])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result: BreakpointState) => {
                this.isDesktopView = !result.matches;
            });

        this.handleCollapsedStatus();

        const showBackButton = this.route.snapshot.queryParams['backButton'];

        if (showBackButton) {
            this.hideBackButton = false;
        }

        this.customerFacade.loaded$.pipe(take(1)).subscribe((loaded) => {
            if (!loaded) {
                this.customerFacade.getCustomerProfile();
            }
        });
    }

    handleCollapsedStatus(): void {
        if (this.env.DISRUPTION_MESSAGES?.COLLAPSE_SYSTEM_STATUS !== undefined) {
            this.showAccordionSummary = !this.env.DISRUPTION_MESSAGES?.COLLAPSE_SYSTEM_STATUS;
            this.isCollapsedSummary = this.env.DISRUPTION_MESSAGES?.COLLAPSE_SYSTEM_STATUS ? '' : 'summary';

            if (this.countActiveAlert === 0) {
                this.showAccordionSummary = false;
                this.isCollapsedSummary = '';
            }
        }

        if (this.env.DISRUPTION_MESSAGES?.COLLAPSE_FILTER_ALERTS !== undefined) {
            this.showAccordionFilters = !this.env.DISRUPTION_MESSAGES?.COLLAPSE_FILTER_ALERTS;
            this.isCollapsedFilters = this.env.DISRUPTION_MESSAGES?.COLLAPSE_FILTER_ALERTS ? '' : 'filters';
        }
    }

    async openModal(modalType: string) {
        this.analyticsService.eventTrack(TrackedEventAction.OPENED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: `${this.FILTER_ALERTS} - ${modalType}`
        });
        if (!this.categories?.length && !this.routes?.length && !this.directions?.length) {
            console.warn('user tried to open too soon');
            return;
        }
        if (this.isModalOpen) return;
        this.isModalOpen = true;
        try {
            let checkboxValues = {};
            let cssClass: string;
            switch (modalType) {
                case this.disruptionMessagesModalType.CATEGORY:
                    checkboxValues = this.categories.reduce((obj, option) => {
                        obj[option] = this.categories.length === 1 || this.chosenCategories.includes(option);
                        return obj;
                    }, {});
                    cssClass = 'disruption-messages-modal-category';
                    break;
                case this.disruptionMessagesModalType.ROUTE:
                    checkboxValues = this.routes.reduce((obj, option) => {
                        obj[option.routeShortName] = this.chosenRoutes.includes(option.routeShortName);
                        return obj;
                    }, {});
                    cssClass = 'disruption-messages-modal-route';
                    break;
                case this.disruptionMessagesModalType.DIRECTION:
                    checkboxValues = this.directions.reduce((obj, direction) => {
                        obj[direction] = this.chosenDirections.includes(direction);
                        return obj;
                    }, {});
                    cssClass = 'disruption-messages-modal-direction';
                    break;
            }
            const modal = await this.modalCtrl.create({
                component: DisruptionMessagesFilterModalComponent,
                cssClass: cssClass,
                componentProps: {
                    checkboxValues: checkboxValues,
                    modalType: modalType
                }
            });
            await modal.present();
            const { data, role } = await modal.onWillDismiss();
            if (role === 'confirm') {
                switch (modalType) {
                    case this.disruptionMessagesModalType.CATEGORY:
                        this.chosenCategories = Object.keys(data).filter((key) => data[key]);
                        break;
                    case this.disruptionMessagesModalType.ROUTE:
                        this.chosenRoutes = Object.keys(data).filter((key) => data[key]);
                        this.chosenDirections = [];
                        break;
                    case this.disruptionMessagesModalType.DIRECTION:
                        this.chosenDirections = Object.keys(data).filter((key) => data[key]);
                        break;
                }
                if (this.env.WMB?.DISRUPTION_MESSAGES) {
                    return this.getMessages();
                }
                this.filterMessages();
                this.cdr.detectChanges();
            }
        } finally {
            this.isModalOpen = false;
        }
    }

    get arrowIcon() {
        return this.isVastmanlandApp ? 'caret-down' : 'chevron-down';
    }

    get isVastmanlandApp() {
        return this.env.APP_NAME === 'vastmanland-app';
    }

    handleBackClicked(): void {
        window.history.back();
    }

    accordionSummaryClicked(event: CustomEvent) {
        this.showAccordionSummary = !!event.detail.value;
    }

    accordionFiltersClicked(event: CustomEvent) {
        const btnTitle = this.showAccordionFilters ? 'Hide' : 'Show';
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.DISTURBANCES,
            name: `${this.FILTER_ALERTS} - ${btnTitle}`
        });

        this.showAccordionFilters = !!event.detail.value;
    }

    getCategoriesTranslation() {
        return this.chosenCategories.map((v) => this.translate.instant(v)).join(', ');
    }

    upcomingAlertsToggle(event: ToggleCustomEvent) {
        this.upcomingAlerts = event.target.checked;
        if (this.env.WMB?.DISRUPTION_MESSAGES) {
            return this.getMessages();
        }
        this.filterMessages();
    }
    public changeDate() {
        console.warn(this.dateValue);
        this.date = this.dateValue.datetime ? Temporal.PlainDate.from(this.dateValue.datetime) : null;
        this.getMessages();
    }

    private getMessages(): void {
        const params = this.env.WMB?.DISRUPTION_MESSAGES
            ? {
                  routeIds:
                      this.chosenRoutes?.length > 0
                          ? this.routes
                                .filter(({ routeShortName }) => this.chosenRoutes.includes(routeShortName))
                                .map(({ routeId }) => routeId?.toString())
                          : [],
                  tripHeadsign: this.chosenDirections.length ? this.chosenDirections : []
              }
            : undefined;

        this.disruptionMessagesService
            .getMessages(params)
            .pipe(take(1))
            .subscribe((response) => {
                this.messagesPackage = response;
                this.messages = this.messagesPackage.messages;
                this.filterMessages();
                this.cdr.detectChanges();
            });
    }

    private filterMessages(): void {
        if (this.allMessages) {
            // Date picker picks start of day, but sometimes alert appears in same day (but later in the day)
            // For this reason we include same day alerts in results, the view is on day basis, not exact time
            const messagesValidByDate = this.allMessages.filter((message) => {
                if (this.date) {
                    const activePeriodStart = Temporal.Instant.from(
                        String(message.activePeriodStart)
                    ).toZonedDateTimeISO(this.timeZone);
                    const activePeriodEnd = Temporal.Instant.from(String(message.activePeriodEnd)).toZonedDateTimeISO(
                        this.timeZone
                    );
                    const activePeriodStartOfDay = activePeriodStart.startOfDay();
                    const dateTimeStartOfDay = this.date?.toZonedDateTime(this.timeZone).startOfDay();
                    const dateTime = this.date.toZonedDateTime(this.timeZone);

                    return (
                        (dateTime.epochMilliseconds >= activePeriodStart.epochMilliseconds ||
                            activePeriodStartOfDay.epochMilliseconds === dateTimeStartOfDay.epochMilliseconds) &&
                        dateTime.epochMilliseconds <= activePeriodEnd.epochMilliseconds
                    );
                } else {
                    return true;
                }
            });
            if (messagesValidByDate.length > 0) {
                this.messagesSummary = this.disruptionMessagesService.getSummary(messagesValidByDate);
                this.categories = this.disruptionMessagesService.getCategories(messagesValidByDate);
                this.routes = this.disruptionMessagesService.getRoutes(this.allMessages);
            }

            this.messages = messagesValidByDate.filter((message) => {
                return (
                    (this.chosenCategories.length === 0 || this.chosenCategories.includes(message.messageType)) &&
                    (this.chosenRoutes.length === 0 ||
                        this.chosenRoutes.some((route) =>
                            message.informedEntities.some((entity) => entity.routeShortName?.trim() === route?.trim())
                        ))
                );
            });

            this.messages = this.messages.filter((message) => {
                return message.informedEntities.some((entity) => {
                    const routeOk =
                        this.chosenRoutes.length === 0 || this.chosenRoutes.includes(entity.routeShortName?.trim());
                    const directionOk =
                        this.chosenDirections.length === 0 ||
                        this.chosenDirections.includes(entity.tripHeadsign?.trim());
                    return routeOk && directionOk;
                });
            });

            const messagesOnChosenRoutes = this.messages
                .map((message) => ({
                    ...message,
                    informedEntities: message.informedEntities.filter((entity) =>
                        this.chosenRoutes.includes(entity.routeShortName?.trim())
                    )
                }))
                .filter((message) => message.informedEntities.length > 0);

            this.directions = this.disruptionMessagesService.getDirection(messagesOnChosenRoutes);
        }
    }

    private setupNotificationClickHandler() {
        this.notificationClick$
            .pipe(
                withLatestFrom(this.isLoggedIn$, this.customerFacade.profile$),

                switchMap(async ([event, isLoggedIn, profile]) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (!isLoggedIn) {
                        const toast = await this.toastController.create({
                            message: 'ⓘ ' + this.translate.instant('TOOLTIP_LOGIN_REQUIRED'),
                            duration: 3000,
                            position: 'top',
                            cssClass: 'custom-toast'
                        });
                        await toast.present();
                    } else if (profile.p_code && !profile.email?.trim()) {
                        this.emailModalService.present(this.emailModal);
                    } else {
                        await this.router.navigate([this.translate.currentLang, 'account', 'add-subscriptions']);
                    }
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();
    }

    onNotificationClick(event: Event) {
        this.notificationClick$.next(event);
    }

    ngOnDestroy() {
        this.notificationClick$.complete();
    }
}
